import React from "react";
import _ from "lodash";
import { useSelector } from "react-redux";
import dateformat from "dateformat";
import {
  AnalyticsCard,
  AnalyticsTable,
  AssetNameCell,
  AssetTrendCell,
  Cell,
  ExportCsvButton,
  TableFooter,
} from "../../common";
import { useAssetAnalyticsQuery } from "src/query/experiences/leadsAndReports/reports";
import { reportsApi } from "src/services/experiences/leadsAndReports/reports";
import { useTableState } from "../../hooks/useTableState";
import { formatDurationWithLabel, safeDivide } from "../../utils/format";
import { ASSET_ANALYTICS_COLUMNS, ASSET_ANALYTICS_CSV_HEADERS } from "../../constants";
import styles from "../../common/common.module.scss";

interface AssetAnalyticsProps {
  storyboardId: string;
}

const getField = (mapping: any, key: string) => {
  const fields = _.get(mapping, "fields") || [];
  return _.get(
    fields.find((f: any) => f.name === key),
    "content",
    "",
  );
};

// Format custom fields as "[name: {v1, v2} | name2: {v3}]", matching legacy CSV output.
const formatCustomFields = (customFields: any[]): string => {
  if (!customFields || _.size(customFields) === 0) return "";
  const parts = customFields.map(
    (item: any) =>
      `${item.name}: {${(item.value || []).map((v: any) => v.value).join(", ")}}`,
  );
  return `[${parts.join(" | ")}]`;
};

// Epoch-ms timestamp -> "07 Jun 2026"; blank if missing.
const formatCsvDate = (value: any): string =>
  value == null ? "" : dateformat(value, "dd mmm yyyy");

const AssetRow: React.FC<{ row: any; cacheKey: string; dateRequest: Record<string, unknown> }> = ({
  row,
  cacheKey,
  dateRequest,
}) => {
  return (
    <>
      <AssetNameCell asset={_.get(row, "asset")} />
      <Cell headline={row.views} />
      <Cell headline={row.visitors} />
      <Cell headline={row.leads} />
      <Cell headline={formatDurationWithLabel(safeDivide(row.timeSpent, row.views)).value} />
      <Cell headline={`${((row.bounceRate * 100) / row.views || 0).toFixed(0)}%`} />
      <Cell headline={row.downloads} />
      <AssetTrendCell row={row} cacheKey={cacheKey} extraRequest={dateRequest} />
    </>
  );
};

export const AssetAnalytics: React.FC<AssetAnalyticsProps> = ({ storyboardId }) => {
  const { request, key, page, setPage, chart, enabled } = useTableState(storyboardId);
  const { data, isLoading } = useAssetAnalyticsQuery(request, key, page, enabled);
  const rows = data?.records ?? [];
  const dateRequest = { startDate: request.startDate, endDate: request.endDate };

  // Public host for the "Link to Asset" column; asset.url is a relative /media path.
  const domain = useSelector(
    (state: any) => _.get(state, "experiences.workspaces.workspace.item.defaultDomain.url") || "",
  );

  const mapCsvRows = (records: any[]) =>
    records.map((row) => {
      const asset = _.get(row, "asset");
      const content = _.get(asset, "content");
      return {
        assetTitle: _.get(content, "asset.name", ""),
        contentType: getField(asset, "contentType"),
        assetLink: `${domain}${_.get(asset, "url", "")}`,
        addedDate: formatCsvDate(_.get(content, "createdDate")),
        updatedDate: formatCsvDate(_.get(content, "modifiedDate")),
        views: row.views,
        visitors: row.visitors,
        leads: row.leads,
        avg: formatDurationWithLabel(safeDivide(row.timeSpent, row.views)).value,
        bounce: `${((row.bounceRate * 100) / row.views || 0).toFixed(0)}%`,
        downloads: row.downloads,
        customField: formatCustomFields(_.get(content, "customFields", [])),
        tags: (_.get(content, "tags") || []).join(","),
      };
    });

  return (
    <AnalyticsCard
      title="Asset Analytics"
      count={data?.totalRecordsCount ?? rows.length}
      download={
        <ExportCsvButton
          label="download"
          filename="Asset Analytics.csv"
          headers={ASSET_ANALYTICS_CSV_HEADERS}
          mapRows={mapCsvRows}
          fetchPage={(p, limit) => reportsApi.fetchAssetsForDownload(request, p, limit)}
        />
      }
      footer={
        <TableFooter
          currentChart={chart}
          handlePageChange={setPage}
          pagination={data}
          paginationName="Assets"
        />
      }
    >
      <div className={styles.assetAnalyticsTable}>
        <AnalyticsTable
          columns={ASSET_ANALYTICS_COLUMNS}
          rows={chart === "table_view" ? rows : []}
          loading={isLoading}
          gridTemplate="350px repeat(7, 1fr)"
          renderRow={(row: any) => <AssetRow row={row} cacheKey={key} dateRequest={dateRequest} />}
        />
      </div>
    </AnalyticsCard>
  );
};
